import { saveSystem } from './SaveSystem';
import { rankSystem } from './RankSystem';
import { GameState } from './GameState';
import { GameData } from '../data/GameData';
import { GameConfig, ATTR_SCALE } from '../core/GameConfig';
import { AttributeKey, ShopPurchases } from '../data/types';

/**
 * 跨周目商店系统（单例）
 * 持有结构化购买状态（开局咖位 / 开局属性加成 / 额外特性点），
 * 购买即时结算并持久化到 MetaData.shopPurchases；新游戏开局时应用加成。
 *
 * 数值规范（2026-09-29 随成就批调整，docs/实现说明.md §3.4i + 迁存章 A2 §4.1）：
 *   开局咖位       💎阶梯价 100/200/300/400，最多 4 级 → 开局 Lv.1+rank（上限 Lv.5）
 *                  （原固定 200/级「Lv1→2 与 Lv4→5 同价」不合理，已废）
 *   开局属性加成   💎50/级，每项最多 10 级（+250）
 *   额外特性点     💎100/点，最多 2 点 → 开局特性点数 = 基础 + points（2026-10-21 点数制）
 */
export class ShopSystem {
    private static instance: ShopSystem;

    private constructor() { }

    static getInstance(): ShopSystem {
        if (!this.instance) {
            this.instance = new ShopSystem();
        }
        return this.instance;
    }

    /** 固定单价（💎/单位）与上限：咖位走下方阶梯表，⛔ 不在本表（防后人按单价误读） */
    readonly PRICE = { attrs: 50, points: 100 } as const;
    readonly MAX = { rank: 4, attrs: 10, points: GameConfig.TRAIT_POINTS_SHOP_MAX } as const;
    /**
     * 开局咖位阶梯价（下标 = 已购等级，买下一级的花费）：第一级半价是入门甜头，
     * 买满开局 Lv.5 是奢侈品。调价只改本数组（2026-09-29 茄子定）。
     */
    readonly RANK_STEPS = [100, 200, 300, 400] as const;
    /** 每级属性加成值（原型：每级 +5；批3-A 随 ATTR_SCALE 缩放保持"10 级买满半条尺"占比） */
    readonly ATTR_BONUS_PER_LEVEL = 5 * ATTR_SCALE;

    /** 购买下一级的花费（咖位查阶梯表按已购等级取，其余查单价表） */
    priceOf(kind: keyof ShopPurchases, currentLevel: number): number {
        return kind === 'rank' ? this.RANK_STEPS[currentLevel] : this.PRICE[kind];
    }

    /** 当前已购状态（每次读取最新 MetaData，保证跨周目/页面切换一致） */
    getPurchases(): ShopPurchases {
        const list = saveSystem.loadMeta().shopPurchases;
        if (list.length > 0 && list[0]) {
            return {
                rank: list[0].rank ?? 0,
                attrs: { ...this.emptyAttrs(), ...(list[0].attrs ?? {}) },
                points: list[0].points ?? 0,
            };
        }
        return this.empty();
    }

    /** 空购买状态（无任何加成） */
    empty(): ShopPurchases {
        return { rank: 0, attrs: this.emptyAttrs(), points: 0 };
    }

    private emptyAttrs(): Record<AttributeKey, number> {
        const attrs = {} as Record<AttributeKey, number>;
        GameData.ATTRIBUTE_KEYS.forEach(k => { attrs[k] = 0; });
        return attrs;
    }

    /** 计算某项购买的总花费 */
    spentOn(kind: keyof ShopPurchases): number {
        const p = this.getPurchases();
        if (kind === 'attrs') {
            const sum = Object.values(p.attrs).reduce((a, b) => a + b, 0);
            return sum * this.PRICE.attrs;
        }
        if (kind === 'rank') {
            let sum = 0;
            for (let i = 0; i < p.rank; i++) sum += this.RANK_STEPS[i];
            return sum;
        }
        return p.points * this.PRICE.points;
    }

    /** 全部已消费成就点（重置时退回） */
    totalSpent(): number {
        return (['rank', 'attrs', 'points'] as const).reduce((sum, kind) => sum + this.spentOn(kind), 0);
    }

    /** 能否购买：未达上限 + 成就点足够 */
    canBuy(kind: keyof ShopPurchases, level: number, attr?: AttributeKey): boolean {
        const p = this.getPurchases();
        const meta = saveSystem.loadMeta();
        const current = kind === 'attrs' ? (p.attrs[attr!] ?? 0) : p[kind];
        return current < this.MAX[kind] && meta.achievementPoints >= this.priceOf(kind, current);
    }

    /** 购买一级（即时扣点并持久化）；成功返回 true */
    buy(kind: keyof ShopPurchases, attr?: AttributeKey): boolean {
        if (!this.canBuy(kind, 0, attr)) return false;
        const meta = saveSystem.loadMeta();
        const p = this.getPurchases();
        const current = kind === 'attrs' ? (p.attrs[attr!] ?? 0) : p[kind];
        const cost = this.priceOf(kind, current);

        if (kind === 'attrs') {
            p.attrs[attr!] = current + 1;
        } else if (kind === 'rank') {
            p.rank += 1;
        } else {
            p.points += 1;
        }

        meta.achievementPoints -= cost;
        meta.shopPurchases = [p];
        saveSystem.saveMeta(meta);
        return true;
    }

    /** 降级一级（退回成就点并持久化）；成功返回 true */
    refund(kind: keyof ShopPurchases, attr?: AttributeKey): boolean {
        const p = this.getPurchases();
        const current = kind === 'attrs' ? (p.attrs[attr!] ?? 0) : p[kind];
        if (current <= 0) return false;
        const meta = saveSystem.loadMeta();
        const cost = this.priceOf(kind, current - 1);

        if (kind === 'attrs') {
            p.attrs[attr!] = current - 1;
        } else if (kind === 'rank') {
            p.rank -= 1;
        } else {
            p.points -= 1;
        }

        meta.achievementPoints += cost;
        meta.shopPurchases = [p];
        saveSystem.saveMeta(meta);
        return true;
    }

    /** 重置：全部购买项归零，退回所有已消费成就点 */
    reset(): void {
        const meta = saveSystem.loadMeta();
        meta.achievementPoints += this.totalSpent();
        meta.shopPurchases = [];
        saveSystem.saveMeta(meta);
    }

    // ===== 开局应用（新游戏时调用） =====

    /** 开局咖位：rank = 1 + 已购等级（sync 名气到阈值，上限 Lv.5） */
    applyStartRank(): void {
        const p = this.getPurchases();
        if (p.rank <= 0) return;
        const state = GameState.getInstance();
        const target = Math.min(1 + p.rank, GameConfig.RANK_MAX);
        if (state.player.rank < target) {
            rankSystem.setRank(target);
        }
    }

    /** 开局属性加成：在初始随机值上叠加已购等级（clamp 到 100） */
    applyStartAttributes(): void {
        const p = this.getPurchases();
        const state = GameState.getInstance();
        GameData.ATTRIBUTE_KEYS.forEach(key => {
            const lv = p.attrs[key] ?? 0;
            if (lv > 0) {
                state.addAttribute(key, lv * this.ATTR_BONUS_PER_LEVEL);
            }
        });
    }

    /** 额外特性点：开局特性点数预算 = 基础点数 + 已购点数（商店那条商品，跨周目累积 ⇒ 有上限） */
    extraTraitPoints(): number {
        return Math.min(this.getPurchases().points, this.MAX.points);
    }

    /** 随机池模式是否已解锁：仅活到 40 岁正常退役才解锁（通关一周目奖励） */
    randomPoolUnlocked(): boolean {
        return saveSystem.loadMeta().randomPoolUnlocked === true;
    }
}

/** 导出单例 */
export const shopSystem = ShopSystem.getInstance();
